import { badgeIcon } from "../../utils/badgeIcon";
import { useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Nav, ProgressBar, Row, Spinner } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import api, { mediaUrl } from "../../api/axios";
import ProvinceSelect from "../Common/ProvinceSelect";
import { ProfileSkeleton } from "../Common/Loading/PageSkeletons";
import ActivationGuide from "../Common/ActivationGuide";

const PROFILE_TABS = ["overview", "binder", "tournaments", "badges", "settings"];

function ProfileHome() {
  const [searchParams, setSearchParams] = useSearchParams();
  const avatarInputRef = useRef(null);
  const requestedTab = searchParams.get("tab");
  const [onboardingProgress, setOnboardingProgress] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [badges, setBadges] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [sharingAchievement, setSharingAchievement] = useState("");
  const [summary, setSummary] = useState({ stats: {}, tournaments: [], recentTrades: [] });
  const [activeTab, setActiveTab] = useState(PROFILE_TABS.includes(requestedTab) ? requestedTab : "overview");
  const [form, setForm] = useState({
    handle: "",
    collectorStyle: "set_builder",
    tradeRadiusKm: 25,
    wantsTradeAlerts: true,
    acceptedLegal: false,
    displayName: "",
    bio: "",
    city: "",
    province: "",
    postalCode: "",
    country: "España"
  });
  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("success");
  const [notificationPreferences, setNotificationPreferences] = useState({
    email: true,
    weeklyDigest: true,
    messages: true,
    trades: true,
    matches: true,
    connections: true,
    tournaments: true,
    communities: true,
    products: true
  });
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileAction, setProfileAction] = useState("");
  const [deletionForm, setDeletionForm] = useState({ confirmation: "", password: "" });
  const [deletionRequested, setDeletionRequested] = useState(false);

  useEffect(() => {
    setActiveTab(PROFILE_TABS.includes(requestedTab) ? requestedTab : "overview");
  }, [requestedTab]);

  useEffect(() => {
    api.get("/users/me").then(({ data }) => {
      setProfile(data);
      setForm(current => ({
        ...current,
        handle: data.handle || "",
        displayName: data.display_name || "",
        bio: data.bio || "",
        city: data.city || "",
        province: data.province || "",
        postalCode: data.postal_code || "",
        country: "España",
        collectorStyle: data.collector_style || current.collectorStyle,
        tradeRadiusKm: data.trade_radius_km || current.tradeRadiusKm,
        wantsTradeAlerts: data.wants_trade_alerts !== false,
        acceptedLegal: Boolean(data.accepted_legal_at)
      }));
    }).catch(() => setProfile(null)).finally(() => setProfileLoading(false));
    api.get("/users/me/badges").then(({ data }) => setBadges(data)).catch(() => setBadges([]));
    api.get("/users/me/achievements").then(({ data }) => setAchievements(data)).catch(() => setAchievements([]));
    api.get("/users/me/summary").then(({ data }) => setSummary(data)).catch(() => setSummary({ stats: {}, tournaments: [], recentTrades: [] }));
    api.get("/users/me/onboarding-progress").then(({ data }) => setOnboardingProgress(data)).catch(() => setOnboardingProgress(null));
    api.get("/notifications/preferences").then(({ data }) => {
      setNotificationPreferences(current => ({ ...current, ...(data || {}) }));
    }).catch(() => {});
    api.get("/users/me/deletion-request").then(({ data }) => {
      setDeletionRequested(data?.status === "pending");
    }).catch(() => {});
  }, []);

  const saveOnboarding = async event => {
    event.preventDefault();
    setMessage("");
    if (!profile?.accepted_legal_at && !form.acceptedLegal) {
      setMessageVariant("danger");
      setMessage("Debes aceptar los terminos y la politica de privacidad para guardar tus preferencias.");
      return;
    }
    setSavingProfile(true);
    try {
      const { data: updatedProfile } = await api.patch("/users/me", {
        displayName: form.displayName,
        bio: form.bio,
        city: form.city,
        province: form.province,
        postalCode: form.postalCode,
        country: "España"
      });
      const { data } = await api.post("/users/me/onboarding", {
        handle: form.handle,
        favoriteGames: ["pokemon"],
        preferences: {
          favoriteGameSlug: "pokemon",
          favoriteSets: [],
          collectorStyle: form.collectorStyle,
          tradeRadiusKm: Number(form.tradeRadiusKm),
          wantsTradeAlerts: form.wantsTradeAlerts
        },
        acceptedLegal: Boolean(profile?.accepted_legal_at || form.acceptedLegal)
      });
      const [{ data: progress }, { data: refreshedBadges }] = await Promise.all([
        api.get("/users/me/onboarding-progress"),
        api.get("/users/me/badges")
      ]);
      setOnboardingProgress(progress);
      setBadges(refreshedBadges || []);
      setProfile(current => ({ ...current, ...updatedProfile, ...data, collector_style: form.collectorStyle, trade_radius_km: Number(form.tradeRadiusKm), wants_trade_alerts: form.wantsTradeAlerts, accepted_legal_at: current?.accepted_legal_at || new Date().toISOString(), city: updatedProfile.city, province: updatedProfile.province, postal_code: updatedProfile.postal_code, country: updatedProfile.country }));
      setMessageVariant("success");
      setMessage("Perfil y preferencias guardados.");
    } catch (error) {
      setMessageVariant("danger");
      setMessage(error?.response?.data?.error || "No pudimos guardar el perfil y las preferencias.");
    } finally {
      setSavingProfile(false);
    }
  };

  const saveNotificationPreferences = async () => {
    setSavingNotifications(true);
    setMessage("");
    try {
      const { data } = await api.patch("/notifications/preferences", notificationPreferences);
      setNotificationPreferences(current => ({ ...current, ...(data || {}) }));
      setMessageVariant("success");
      setMessage("Preferencias de notificacion guardadas.");
    } catch {
      setMessageVariant("danger");
      setMessage("No pudimos guardar las preferencias de notificacion.");
    } finally {
      setSavingNotifications(false);
    }
  };

  const resendVerification = async () => {
    setProfileAction("verification");
    setMessage("");
    try {
      const { data } = await api.post("/auth/resend-verification");
      setMessageVariant("success");
      setMessage(data.alreadyVerified ? "Tu email ya esta verificado." : "Enviamos un nuevo enlace de verificacion.");
    } catch {
      setMessageVariant("danger");
      setMessage("No pudimos reenviar el correo de verificacion.");
    } finally {
      setProfileAction("");
    }
  };

  const exportMyData = async () => {
    setProfileAction("export");
    setMessage("");
    try {
      const { data } = await api.get("/users/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "cardora-data.json";
      anchor.click();
      URL.revokeObjectURL(url);
      setMessageVariant("success");
      setMessage("Exportacion preparada.");
    } catch {
      setMessageVariant("danger");
      setMessage("No pudimos exportar tus datos.");
    } finally {
      setProfileAction("");
    }
  };

  const requestDeletion = async () => {
    setProfileAction("delete");
    setMessage("");
    try {
      const { data } = await api.post("/users/me/deletion-request", deletionForm);
      setDeletionRequested(true);
      setMessageVariant("warning");
      setMessage("Solicitud registrada. Eliminacion prevista para " + new Date(data.scheduled_for).toLocaleDateString("es-ES") + ".");
    } catch (error) {
      setMessageVariant("danger");
      setMessage(error?.response?.data?.error || "No pudimos registrar la solicitud.");
    } finally {
      setProfileAction("");
    }
  };

  const cancelDeletion = async () => {
    setProfileAction("cancel-delete");
    try {
      await api.delete("/users/me/deletion-request");
      setDeletionRequested(false);
      setDeletionForm({ confirmation: "", password: "" });
      setMessageVariant("success");
      setMessage("Solicitud de eliminacion cancelada.");
    } catch {
      setMessageVariant("danger");
      setMessage("No pudimos cancelar la solicitud.");
    } finally {
      setProfileAction("");
    }
  };

  const uploadAvatar = async file => {
    if (!file) return;
    setProfileAction("avatar");
    const formData = new FormData();
    formData.append("avatar", file);
    try {
      const { data } = await api.post("/users/me/avatar", formData);
      setProfile(current => ({ ...current, ...data }));
      const storedUser = JSON.parse(localStorage.getItem("user") || "null");
      localStorage.setItem("user", JSON.stringify({ ...(storedUser || {}), ...data }));
      setMessageVariant("success");
      setMessage("Avatar actualizado.");
    } catch {
      setMessageVariant("danger");
      setMessage("No se pudo subir el avatar.");
    } finally {
      setProfileAction("");
    }
  };

  const shareAchievement = async achievement => {
    setSharingAchievement(achievement.id); setMessage("");
    try { await api.post(`/users/me/achievements/${achievement.id}/share`); setAchievements(current => current.map(item => item.id === achievement.id ? { ...item, shared_at: new Date().toISOString() } : item)); setMessageVariant("success"); setMessage("Logro compartido en el feed."); }
    catch (error) { setMessageVariant("danger"); setMessage(error?.response?.data?.error || "No se pudo compartir el logro."); }
    finally { setSharingAchievement(""); }
  };

  const stats = summary.stats || {};
  const profileChecklist = [
    { key: "avatar", label: "Foto", complete: Boolean(profile?.avatar_url) },
    { key: "bio", label: "Presentacion", complete: Boolean(profile?.bio?.trim()) },
    { key: "handle", label: "Usuario publico", complete: Boolean(profile?.handle?.trim()) },
    { key: "city", label: "Ubicacion", complete: Boolean(profile?.city?.trim()) },
    { key: "preferences", label: "Preferencias", complete: Boolean(profile?.collector_style) }
  ];
  const profileCompletedItems = profileChecklist.filter(item => item.complete).length;
  const profileCompletion = Math.round((profileCompletedItems / profileChecklist.length) * 100);
  const openProfileSettings = () => {
    setActiveTab("settings");
    setSearchParams({ tab: "settings" });
  };
  const metricCards = [
    ["Cartas", stats.cards_count || 0],
    ["Para trade", stats.cards_for_trade || 0],
    ["Buscadas", stats.wanted_cards || 0],
    ["Likes binder", stats.binder_likes || 0],
    ["Experiencias OK", stats.completed_trades || 0],
    ["Valoracion", stats.average_rating ? `${stats.average_rating}/5` : "Sin reviews"],
    ["Eventos", stats.tournaments_played || 0]
  ];

  if (profileLoading) return <Container className="page-band profile-page"><ProfileSkeleton /></Container>;

  return (
    <Container className="page-band profile-page">
      <div className="profile-hero mb-4">
        <button type="button" className="profile-avatar profile-avatar-edit" onClick={() => avatarInputRef.current?.click()} aria-label="Cambiar foto de perfil" disabled={profileAction === "avatar"}>
          {profile?.avatar_url ? <img src={mediaUrl(profile.avatar_url)} alt="" /> : <span>{profile?.display_name?.slice(0, 1) || "C"}</span>}
          <span className="profile-avatar-edit-icon">{profileAction === "avatar" ? <Spinner size="sm" /> : <i className="bi bi-pencil-fill" />}</span>
        </button>
        <input ref={avatarInputRef} type="file" accept="image/*" className="visually-hidden" onChange={event => uploadAvatar(event.target.files?.[0])} />
        <div className="profile-identity-copy">
          <div className="profile-title-edit-row"><h1 className="section-title mb-1">{profile?.display_name || "Perfil coleccionista"}</h1><Button type="button" variant="link" className="profile-edit-trigger" onClick={openProfileSettings} aria-label="Editar perfil" title="Editar perfil"><i className="bi bi-pencil" /></Button></div>
          <p className="muted-copy mb-2">{profile?.bio || "Coleccionista Pokemon TCG"}</p>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            {profile?.handle && <Badge bg="dark">@{profile.handle}</Badge>}
            <Badge bg="light" text="dark">{profile?.onboarding_completed ? "Perfil listo" : "Pendiente completar"}</Badge>
            <Badge bg="light" text="dark">{stats.tournament_points || 0} pts evento</Badge>
            <Badge bg="success">{stats.average_rating ? `${stats.average_rating}/5 trade rating` : "Sin valoraciones"}</Badge>
          </div>
        </div>
        <div className="profile-actions">
          <Button as={Link} to="/binder" className="btn-cardora">Gestionar binder</Button>
          <Button as={Link} to={profile?.handle ? `/u/${profile.handle}` : `/u/${profile?.id}`} variant="outline-dark">Ver publico</Button>
        </div>
      </div>

      {profile && !profile.email_verified_at && (
        <Alert variant="warning" className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <span>Verifica tu email para proteger tu cuenta y asegurar que recibes matches y conexiones.</span>
          <Button variant="outline-dark" size="sm" disabled={profileAction === "verification"} onClick={resendVerification}>{profileAction === "verification" && <Spinner size="sm" className="me-2" />}Reenviar verificacion</Button>
        </Alert>
      )}

      {profileCompletion < 100 && (
        <Card className="cardora-card profile-completion-card mb-4">
          <Card.Body>
            <div className="profile-completion-head">
              <div><span className="profile-completion-kicker">Completa tu perfil</span><h2>Ayuda a que otros coleccionistas te reconozcan</h2><p>Un perfil completo da mas contexto cuando aparece un match.</p></div>
              <strong>{profileCompletion}%</strong>
            </div>
            <ProgressBar now={profileCompletion} aria-label={`Perfil completado al ${profileCompletion}%`} />
            <div className="profile-completion-items">
              {profileChecklist.map(item => <span className={item.complete ? "is-complete" : ""} key={item.key}><i className={`bi ${item.complete ? "bi-check-circle-fill" : "bi-circle"}`} />{item.label}</span>)}
            </div>
            <Button type="button" className="btn-cardora" size="sm" onClick={openProfileSettings}>Completar ahora <i className="bi bi-arrow-right ms-1" /></Button>
          </Card.Body>
        </Card>
      )}
      <Nav variant="tabs" activeKey={activeTab} onSelect={key => { const next = key || "overview"; setActiveTab(next); setSearchParams(next === "overview" ? {} : { tab: next }); }} className="binder-tabs mb-4">
        <Nav.Item><Nav.Link eventKey="overview">Resumen</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="binder">Binder</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="tournaments">Eventos</Nav.Link></Nav.Item>
        {onboardingProgress?.badgesEnabled !== false && <Nav.Item><Nav.Link eventKey="badges">Medallas</Nav.Link></Nav.Item>}
        <Nav.Item><Nav.Link eventKey="settings">Preferencias</Nav.Link></Nav.Item>
      </Nav>

      {activeTab === "overview" && (
        <>
        {onboardingProgress?.enabled && !onboardingProgress.isComplete && (
          <ActivationGuide progress={onboardingProgress} expandedDefault />
        )}
        <div className="profile-private-summary-head">
          <div><h2>Tu resumen</h2><p>Esta vista es privada. En tu perfil publico solo aparecen coleccion, actividad, reputacion, eventos y medallas desbloqueadas.</p></div>
          <Button as={Link} to={profile?.handle ? `/u/${profile.handle}` : `/u/${profile?.id}`} variant="outline-dark" size="sm">Ver como me ven</Button>
        </div>
        <Row className="g-3">
          {metricCards.map(([label, value]) => (
            <Col sm={6} xl={4} key={label}>
              <div className="store-metric">
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            </Col>
          ))}
        </Row>
        </>
      )}

      {activeTab === "binder" && (
        <Row className="g-3">
          <Col lg={4}><div className="store-metric"><span>Colecciones</span><strong>{stats.collections_count || 0}</strong></div></Col>
          <Col lg={4}><div className="store-metric"><span>Cartas publicadas</span><strong>{stats.cards_count || 0}</strong></div></Col>
          <Col lg={4}><div className="store-metric"><span>Likes</span><strong>{stats.binder_likes || 0}</strong></div></Col>
          <Col xs={12}>
            <Card className="cardora-card">
              <Card.Body className="d-flex flex-column flex-md-row justify-content-between gap-3 align-items-md-center">
                <div>
                  <Card.Title>Tu binder</Card.Title>
                  <Card.Text className="muted-copy mb-0">Administra colecciones, cartas para conectar y buscadas.</Card.Text>
                </div>
                <Button as={Link} to="/binder" className="btn-cardora">Abrir binder</Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {activeTab === "tournaments" && (
        <Card className="cardora-card">
          <Card.Body>
            <Card.Title>Ultimos eventos</Card.Title>
            <div className="admin-list">
              {summary.tournaments?.length === 0 && <div className="muted-copy">Todavia no hay eventos registrados.</div>}
              {summary.tournaments?.map(tournament => (
                <Link className="profile-event-row" to={`/store/${tournament.business_slug}`} key={tournament.tournament_id}>
                  <div>
                    <strong>{tournament.tournament_name}</strong>
                    <span>{tournament.business_name} - {tournament.starts_at ? new Date(tournament.starts_at).toLocaleDateString("es-ES") : "Fecha por confirmar"}</span>
                  </div>
                  <Badge bg="light" text="dark">{tournament.points} pts</Badge>
                </Link>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {activeTab === "badges" && onboardingProgress?.badgesEnabled !== false && (
        <Card className="cardora-card">
          <Card.Body>
            <Card.Title>Medallas</Card.Title>
            <div className="badge-grid">
              {badges.length === 0 && <span className="muted-copy">Aun no hay medallas desbloqueadas.</span>}
              {badges.map(badge => (
                <div className="pokemon-badge" key={badge.id}>
                  <i aria-hidden="true" className={`bi bi-${badgeIcon(badge)}`} />
                  <div>
                    <strong>{badge.name}</strong>
                    <span>{badge.description}</span>
                  </div>
                </div>
              ))}
            </div>
            <hr className="my-4" />
            <div className="d-flex justify-content-between align-items-end gap-3 mb-3">
              <div><Card.Title className="mb-1">Logros</Card.Title><p className="muted-copy mb-0">Progreso de tu coleccion y participacion en la comunidad.</p></div>
              <Badge bg="dark">{achievements.filter(item => item.unlocked_at).length}/{achievements.length}</Badge>
            </div>
            {message && <Alert variant={messageVariant}>{message}</Alert>}
            <div className="achievement-grid">
              {achievements.map(achievement => {
                const unlocked = Boolean(achievement.unlocked_at);
                const percentage = Math.min(100, Math.round((Number(achievement.progress || 0) / Math.max(1, Number(achievement.target || 1))) * 100));
                return <article className={`achievement-card ${unlocked ? "is-unlocked" : ""}`} key={achievement.id}>
                  <div className="achievement-icon"><i className={`bi bi-${achievement.icon_key || "award"}`} /></div>
                  <div className="achievement-copy">
                    <div className="d-flex justify-content-between gap-2"><strong>{achievement.name}</strong>{unlocked && <Badge bg="success">Desbloqueado</Badge>}</div>
                    {achievement.context_label && <small>{achievement.context_label}</small>}
                    <span>{achievement.description}</span>
                    <ProgressBar now={percentage} aria-label={`Progreso de ${achievement.name}`} />
                    <small>{achievement.progress}/{achievement.target}</small>
                  </div>
                  {unlocked && <Button size="sm" variant={achievement.shared_at ? "outline-secondary" : "outline-dark"} disabled={Boolean(achievement.shared_at) || sharingAchievement === achievement.id} onClick={() => shareAchievement(achievement)}><i className={`bi ${achievement.shared_at ? "bi-check2" : "bi-share"} me-1`} />{achievement.shared_at ? "Compartido" : "Compartir"}</Button>}
                </article>;
              })}
            </div>
          </Card.Body>
        </Card>
      )}

      {activeTab === "settings" && (
          <Card className="cardora-card">
            <Card.Body>
              <h2 className="h5">Preferencias Pokemon</h2>
              {message && <Alert variant={messageVariant}>{message}</Alert>}
              <div className="profile-avatar-upload mb-3">
                <div className="profile-avatar small">
                  {profile?.avatar_url ? <img src={mediaUrl(profile.avatar_url)} alt="" /> : <span>{profile?.display_name?.slice(0, 1) || "C"}</span>}
                </div>
                <div>
                  <Form.Label>Foto de perfil</Form.Label>
                  <Form.Control type="file" accept="image/*" disabled={profileAction === "avatar"} onChange={event => uploadAvatar(event.target.files?.[0])} />
                  {profileAction === "avatar" && <small className="muted-copy d-flex align-items-center gap-2 mt-2"><Spinner size="sm" />Subiendo imagen...</small>}
                </div>
              </div>
              <Form onSubmit={saveOnboarding}>
                <Row className="g-3">
                  <Col md={6}><Form.Label>Nombre visible</Form.Label><Form.Control required value={form.displayName} onChange={event => setForm({ ...form, displayName: event.target.value })} /></Col>
                  <Col md={6}><Form.Label>Ciudad o municipio</Form.Label><Form.Control required value={form.city} onChange={event => setForm({ ...form, city: event.target.value })} autoComplete="address-level2" /></Col>
                  <Col md={6}><Form.Label>Provincia</Form.Label><ProvinceSelect value={form.province} onChange={event => setForm({ ...form, province: event.target.value })} /></Col>
                  <Col md={6}><Form.Label>Codigo postal <span className="muted-copy">(privado)</span></Form.Label><Form.Control value={form.postalCode} inputMode="numeric" maxLength={5} pattern="[0-9]{5}" onChange={event => setForm({ ...form, postalCode: event.target.value.replace(/\D/g, "").slice(0, 5) })} autoComplete="postal-code" /></Col>
                  <Col xs={12}><Form.Label>Bio</Form.Label><Form.Control as="textarea" rows={2} maxLength={500} value={form.bio} onChange={event => setForm({ ...form, bio: event.target.value })} /></Col>
                  <Col md={6}>
                    <Form.Label>Handle publico</Form.Label>
                    <Form.Control
                      value={form.handle}
                      onChange={event => setForm({ ...form, handle: event.target.value })}
                      placeholder="ash-ketchum"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Estilo coleccionista</Form.Label>
                    <Form.Select
                      value={form.collectorStyle}
                      onChange={event => setForm({ ...form, collectorStyle: event.target.value })}
                    >
                      <option value="set_builder">Completar sets</option>
                      <option value="rare_hunter">Cazar rarezas</option>
                      <option value="trader">Trade activo</option>
                      <option value="sealed_collector">Sellado y boosters</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Radio de trade</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="500"
                      value={form.tradeRadiusKm}
                      onChange={event => setForm({ ...form, tradeRadiusKm: event.target.value })}
                    />
                  </Col>
                  <Col md={6} className="d-flex align-items-end">
                    <Form.Check
                      type="switch"
                      label="Avisarme de oportunidades de trade"
                      checked={form.wantsTradeAlerts}
                      onChange={event => setForm({ ...form, wantsTradeAlerts: event.target.checked })}
                    />
                  </Col>
                </Row>
                {!profile?.accepted_legal_at && <div className="profile-legal-consent mt-3"><Form.Check type="checkbox" id="profile-legal-consent" checked={form.acceptedLegal} onChange={event => setForm(current => ({ ...current, acceptedLegal: event.target.checked }))} label={<span>Acepto los <Link to="/legal/terms" target="_blank">terminos de uso</Link> y la <Link to="/legal/privacy" target="_blank">politica de privacidad</Link>.</span>} /><small className="muted-copy">Necesario una sola vez para completar tu perfil.</small></div>}
                <Button type="submit" className="btn-cardora mt-3" disabled={savingProfile}>{savingProfile && <Spinner size="sm" className="me-2" />}{savingProfile ? "Guardando..." : "Guardar preferencias"}</Button>
              </Form>
              <hr className="my-4" />
              <h2 className="h5">Notificaciones</h2>
              <p className="muted-copy">Elige que avisos quieres recibir. Desactivar email afecta a todos los correos no esenciales.</p>
              <Row className="g-2">
                {[
                  ["email", "Recibir avisos por email"],
                  ["weeklyDigest", "Resumen semanal de coincidencias e intereses"],
                  ["matches", "Nuevos matches"],
                  ["connections", "Solicitudes y conexiones"],
                  ["messages", "Mensajes privados"],
                  ["trades", "Intereses e intercambios"],
                  ["communities", "Comunidades"],
                  ["tournaments", "Eventos y torneos"],
                  ["products", "Productos de tiendas"]
                ].map(([key, label]) => (
                  <Col md={6} key={key}>
                    <Form.Check
                      type="switch"
                      id={"notification-" + key}
                      label={label}
                      checked={notificationPreferences[key] !== false}
                      onChange={event => setNotificationPreferences(current => ({ ...current, [key]: event.target.checked }))}
                    />
                  </Col>
                ))}
              </Row>
              <Button type="button" variant="outline-dark" className="mt-3" disabled={savingNotifications} onClick={saveNotificationPreferences}>
                {savingNotifications && <Spinner size="sm" className="me-2" />}{savingNotifications ? "Guardando..." : "Guardar notificaciones"}
              </Button>
              <hr className="my-4" />
              <h2 className="h5">Tus datos y cuenta</h2>
              <p className="muted-copy">Puedes descargar una copia estructurada de tus datos o solicitar la eliminacion de la cuenta.</p>
              <Button type="button" variant="outline-dark" disabled={profileAction === "export"} onClick={exportMyData}>{profileAction === "export" ? <Spinner size="sm" className="me-2" /> : <i className="bi bi-download me-2" />}{profileAction === "export" ? "Preparando..." : "Descargar mis datos"}</Button>
              <div className="account-danger-zone mt-3">
                {deletionRequested ? (
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span>La eliminacion esta solicitada.</span>
                    <Button type="button" size="sm" variant="outline-dark" disabled={profileAction === "cancel-delete"} onClick={cancelDeletion}>{profileAction === "cancel-delete" && <Spinner size="sm" className="me-2" />}Cancelar solicitud</Button>
                  </div>
                ) : (
                  <>
                    <strong>Solicitar eliminacion</strong>
                    <p className="muted-copy small">Tendras 14 dias para cancelar. Despues se procesara la eliminacion y anonimizacion de los datos que debamos conservar.</p>
                    <Row className="g-2">
                      <Col md={5}><Form.Control placeholder="Escribe ELIMINAR" value={deletionForm.confirmation} onChange={event => setDeletionForm(current => ({ ...current, confirmation: event.target.value }))} /></Col>
                      <Col md={5}><Form.Control type="password" placeholder="Contraseña actual" value={deletionForm.password} onChange={event => setDeletionForm(current => ({ ...current, password: event.target.value }))} /></Col>
                      <Col md={2}><Button type="button" variant="outline-danger" className="w-100" disabled={deletionForm.confirmation.trim().toUpperCase() !== "ELIMINAR" || profileAction === "delete"} onClick={requestDeletion}>{profileAction === "delete" && <Spinner size="sm" className="me-2" />}{profileAction === "delete" ? "Enviando..." : "Solicitar"}</Button></Col>
                    </Row>
                  </>
                )}
              </div>
            </Card.Body>
          </Card>
      )}
    </Container>
  );
}

export default ProfileHome;
